import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import {
  ShieldCheck,
  RefreshCw,
  Building2,
  CheckCircle2,
  FileCheck2,
  Boxes,
  ArrowRight,
  Package,
} from "lucide-react";
import { trpcClient } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatNumber, formatDate } from "@/lib/utils";
import { useVendorLookup } from "@/hooks/useFKLookup";
import { useHeaderSearch } from "@/hooks/useHeaderSearch";

export function PendingIqcQueueTab() {
  const navigate = useNavigate();
  const { headerSearch } = useHeaderSearch();
  const { options: vendorOptions } = useVendorLookup();
  const [selectedVendorId, setSelectedVendorId] = useState<string>("ALL");

  const { data, isLoading, refetch, isRefetching } = useQuery({
    queryKey: [
      "pending-iqc-queue",
      headerSearch,
      selectedVendorId === "ALL" ? undefined : selectedVendorId,
    ],
    queryFn: async () => {
      return await trpcClient.qcMaterials.getPendingQueue.query({
        search: headerSearch.trim() || undefined,
        vendorId: selectedVendorId === "ALL" ? undefined : selectedVendorId,
      });
    },
  });

  const items = data?.items || [];
  const metrics = data?.metrics || {
    totalPendingLots: 0,
    totalPendingQty: 0,
    uniqueVendorsCount: 0,
    uniqueMaterialsCount: 0,
  };

  const handleInspectSingle = (item: any) => {
    navigate({
      to: "/qc-materials/create",
      search: {
        purchaseOrderId: item.purchaseOrderId,
        poCode: item.poCode,
        materialId: item.materialId,
        materialName: item.materialName,
        materialCategory: item.materialCategory,
        vendorId: item.vendorId,
        vendorName: item.vendorName,
        receiptDocId: item.receiptDocId,
        batchNumber: item.batchNumber,
        pendingQty: item.pendingQty,
        orderedQty: item.orderedQty,
        unit: item.unit,
      } as any,
    });
  };

  return (
    <div className="flex flex-col h-full space-y-2">
      {/* 1. Single-Row Inline Metrics Bar (36-38px) */}
      <div className="flex items-center justify-between px-3 py-1.5 bg-gradient-to-r from-blue-50/80 via-white to-slate-50 border border-blue-200/80 rounded-md text-xs shadow-xs">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-1.5 font-medium text-blue-900">
            <ShieldCheck className="w-3.5 h-3.5 text-blue-600" />
            <span>Hàng Chờ Kiểm IQC (NVL Về Cổng):</span>
            <span className="font-bold text-blue-700 bg-blue-100/80 px-1.5 py-0.5 rounded-full text-[11px] tabular-nums">
              {formatNumber(metrics.totalPendingLots)} lô
            </span>
          </div>

          <div className="h-3.5 w-px bg-blue-200 hidden sm:block" />

          <div className="flex items-center gap-1 text-slate-600">
            <Boxes className="w-3.5 h-3.5 text-slate-500" />
            <span>Tổng lượng chờ kiểm:</span>
            <span className="font-bold text-slate-900 tabular-nums">
              {formatNumber(metrics.totalPendingQty)}
            </span>
          </div>

          <div className="h-3.5 w-px bg-blue-200 hidden md:block" />

          <div className="flex items-center gap-1 text-slate-600">
            <Building2 className="w-3.5 h-3.5 text-indigo-500" />
            <span>Nhà cung cấp:</span>
            <span className="font-semibold text-indigo-700 tabular-nums">
              {formatNumber(metrics.uniqueVendorsCount)} NCC
            </span>
          </div>

          <div className="h-3.5 w-px bg-blue-200 hidden lg:block" />

          <div className="flex items-center gap-1 text-slate-600">
            <Package className="w-3.5 h-3.5 text-amber-500" />
            <span>Chủng loại vật tư:</span>
            <span className="font-semibold text-amber-700 tabular-nums">
              {formatNumber(metrics.uniqueMaterialsCount)} mã
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => refetch()}
            disabled={isRefetching}
            className="h-6 px-2 text-[11px] text-slate-600 hover:text-slate-900"
          >
            <RefreshCw
              className={`w-3 h-3 mr-1 ${isRefetching ? "animate-spin" : ""}`}
            />
            Làm mới
          </Button>
        </div>
      </div>

      {/* 2. Filter Ribbon */}
      <div className="flex items-center justify-between gap-2 bg-white px-3 py-1.5 rounded-md border border-slate-200 text-xs">
        <div className="flex flex-wrap items-center gap-2.5">
          <label className="text-xs font-medium text-slate-600">Nhà cung cấp:</label>
          <select
            value={selectedVendorId}
            onChange={(e) => setSelectedVendorId(e.target.value)}
            className="h-7 px-2 text-xs border border-slate-200 rounded-md bg-slate-50 text-slate-700 focus:outline-none focus:ring-1 focus:ring-indigo-500"
          >
            <option value="ALL">Tất cả Nhà Cung Cấp ({vendorOptions.length})</option>
            {vendorOptions.map((v) => (
              <option key={v.id} value={v.id}>
                {v.label}
              </option>
            ))}
          </select>

          {headerSearch && (
            <span className="text-xs text-slate-500 italic bg-slate-50 px-2 py-0.5 rounded border border-slate-200">
              Từ khóa tìm kiếm: "{headerSearch}"
            </span>
          )}
        </div>
      </div>

      {/* 3. High-Density Enterprise Data-Grid with Solid Sticky Columns */}
      <div className="border border-slate-200 rounded-md bg-white overflow-hidden shadow-xs flex-1 flex flex-col min-h-0">
        <div className="overflow-auto flex-1 max-h-[calc(100vh-270px)]">
          <Table className="w-full text-xs caption-bottom apv-compact-table min-w-[1000px]">
            <TableHeader className="bg-slate-100/90 sticky top-0 z-20 border-b border-slate-200 text-slate-700">
              <TableRow className="hover:bg-transparent">
                <TableHead className="w-[130px] font-semibold text-slate-900 sticky left-0 z-30 bg-slate-100 !bg-white border-r border-slate-200">
                  Mã Đơn Mua (PO)
                </TableHead>
                <TableHead className="font-semibold text-slate-800 min-w-[180px]">
                  Nhà Cung Cấp
                </TableHead>
                <TableHead className="w-[120px] font-semibold text-slate-800">
                  Mã Vật Tư
                </TableHead>
                <TableHead className="font-semibold text-slate-800 min-w-[200px]">
                  Tên Vật Tư & Quy Cách
                </TableHead>
                <TableHead className="w-[110px] font-semibold text-slate-800">
                  Ngày Đặt
                </TableHead>
                <TableHead className="w-[110px] font-semibold text-slate-800">
                  Hạn Giao
                </TableHead>
                <TableHead className="w-[90px] font-semibold text-slate-800 text-right">
                  SL Đặt Mua
                </TableHead>
                <TableHead className="w-[90px] font-semibold text-slate-800 text-right">
                  Đã Kiểm
                </TableHead>
                <TableHead className="w-[110px] font-semibold text-blue-900 text-right bg-blue-50/50">
                  Chờ Kiểm IQC
                </TableHead>
                <TableHead className="w-[110px] text-center font-semibold text-slate-900 sticky right-0 z-30 bg-slate-100 !bg-white border-l border-slate-200">
                  Thao Tác
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={10} className="h-32 text-center text-slate-400">
                    <RefreshCw className="w-5 h-5 mx-auto animate-spin mb-1 text-slate-400" />
                    Đang tải danh sách hàng đợi IQC...
                  </TableCell>
                </TableRow>
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={10}
                    className="h-32 text-center text-slate-500 bg-slate-50/30"
                  >
                    <CheckCircle2 className="w-8 h-8 mx-auto text-emerald-500 mb-2" />
                    <p className="font-medium text-slate-700">
                      Hiện tại không có lô hàng nào chờ kiểm tra IQC tại cổng!
                    </p>
                    <p className="text-[11px] text-slate-400 mt-0.5">
                      Toàn bộ nguyên vật liệu từ các Đơn mua hàng đã được nghiệm thu đầy đủ.
                    </p>
                  </TableCell>
                </TableRow>
              ) : (
                items.map((item: any) => (
                  <TableRow
                    key={item.id}
                    className="hover:bg-slate-50/80 transition-colors"
                  >
                    {/* Mã ĐMH (Solid Sticky) */}
                    <TableCell className="px-2 py-1.5 sticky left-0 z-10 bg-white !bg-white border-r border-slate-200">
                      <span className="font-mono font-semibold text-indigo-700 text-xs">
                        {item.poCode || "PO-UNKNOWN"}
                      </span>
                    </TableCell>

                    {/* Nhà Cung Cấp */}
                    <TableCell className="px-2 py-1.5">
                      <div className="font-medium text-slate-800 leading-tight">
                        {item.vendorName}
                      </div>
                      {item.vendorCode && (
                        <div className="text-[10px] text-slate-400 font-mono">
                          {item.vendorCode}
                        </div>
                      )}
                    </TableCell>

                    {/* Mã Vật Tư */}
                    <TableCell className="px-2 py-1.5 font-mono text-slate-700">
                      {item.materialCode || "-"}
                    </TableCell>

                    {/* Tên & Quy Cách */}
                    <TableCell className="px-2 py-1.5">
                      <div className="font-medium text-slate-900 leading-tight">
                        {item.materialName}
                      </div>
                      {item.specs && (
                        <div className="text-[11px] text-slate-500 truncate max-w-[240px]">
                          {item.specs}
                        </div>
                      )}
                    </TableCell>

                    {/* Ngày Đặt */}
                    <TableCell className="px-2 py-1.5 text-slate-600">
                      {formatDate(item.orderDate)}
                    </TableCell>

                    {/* Hạn Giao */}
                    <TableCell className="px-2 py-1.5 text-slate-600">
                      {formatDate(item.expectedDate)}
                    </TableCell>

                    {/* SL Đặt */}
                    <TableCell className="px-2 py-1.5 text-right font-mono text-slate-600 tabular-nums">
                      {formatNumber(item.orderedQty)} {item.unit}
                    </TableCell>

                    {/* Đã Kiểm */}
                    <TableCell className="px-2 py-1.5 text-right font-mono text-slate-500 tabular-nums">
                      {item.inspectedQty > 0 ? (
                        <span className="text-emerald-600 font-medium">
                          {formatNumber(item.inspectedQty)}
                        </span>
                      ) : (
                        "-"
                      )}
                    </TableCell>

                    {/* Chờ Kiểm IQC */}
                    <TableCell className="px-2 py-1.5 text-right font-mono font-bold text-blue-700 bg-blue-50/50 tabular-nums">
                      {formatNumber(item.pendingQty)} {item.unit}
                    </TableCell>

                    {/* Action (Solid Sticky) */}
                    <TableCell className="px-2 py-1 text-center sticky right-0 z-10 bg-white !bg-white border-l border-slate-200">
                      <Button
                        size="sm"
                        onClick={() => handleInspectSingle(item)}
                        className="h-6 px-2 text-[11px] bg-blue-50 hover:bg-blue-100 text-blue-800 border border-blue-300 font-medium"
                        title="Lập Biên bản kiểm IQC cho lô này"
                      >
                        <FileCheck2 className="w-3 h-3 mr-1 text-blue-600" />
                        Lập IQC
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
